import {COLOR_PALETTES} from 'analysis-tool/ui/charting/ChartingConstants';
import {DiscreteColorPalette} from 'analysis-tool/ui/charting/ChartingTypes';
import {assertAndContinue, assert} from 'Asserts';
import {DataFrameColumnDef, DataFrameSpec} from 'data-frames/DataFrameTypes';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStepRawData} from 'pipelines/PipelineTypes';
import {InputDataFrames, InputStepDataFramesMappedType, InputStepDatasRecord} from 'pipelines/StepDataUtils';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {JSONObject, JSONValue} from 'util/Types';

import {TabbedUISchema} from './step-config-settings/StepConfigUISchemas';
// Use Symbols so that there's no possibility that a variable name and these keys conflict in a
// `ShapeUISchema` object.
/**
 * @see documentation in
 * client/pipelines/generalized-step-config-editor/StepUISchemas.tsx _GetJsonSpecUISchemaShape
 */
export const FIELD_ORDER_UI_SCHEMA_KEY = Symbol('FieldOrderUISchemaKey');
/**
 * @see documenatation in
 * client/pipelines/generalized-step-config-editor/StepUISchemas.tsx _GetJsonSpecUISchemaShape
 */
export const HIDDEN_FIELDS_KEY = Symbol('HiddenFieldsSchemaKey');
/**
 * @see documentation in
 * client/pipelines/generalized-step-config-editor/StepUISchemas.tsx _GetJsonSpecUISchemaShape
 */
export const ON_CHANGE_CONFIG_MIDDLEWARE_KEY = Symbol('OnChangeConfigMiddlewareKey');

/**
 * Indicates that the step has backend field logic, and should call the backend step config API when edited.
 *
 * This is currently most suitable for forms with few form controls because every edit to the form will perform
 * a web request, which will block the form for the user temporarily. In particular, do not use this for forms
 * with text inputs, at least until text debouncing is implemented.
 */
export const HAS_BACKEND_FIELD_GENERATOR = Symbol('HasBackendFieldGenerator');

/**
 * @see documentation in
 * client/pipelines/generalized-step-config-editor/StepUISchemas.tsx _GetJsonSpecUISchemaShape
 */
export const CUSTOM_COMPONENT_KEY = Symbol('CustomComponent');

/**
 * Order the tabs in the tabbed container specified by the `TabbedUISchema`.
 */
export const TAB_ORDER_UI_SCHEMA_KEY = Symbol('TabOrderUISchemaKey');

/**
 * The name of the section to display in the tabbed container specified by the `TabbedUISchema`.
 */
export const SECTION_NAME_UI_SCHEMA_KEY = Symbol('SectionNameUISchemaKey');

/**
 * Whether to show a divider line between sections in the tabbed container specified by the
 * `ShapeUISchemaV2`.
 */
export const SHOW_DIVIDER_LINE_KEY = Symbol('ShowDividerLineUISchemaKey');

/**
 * Allows showing a banner at the top of a V2 schema editor
 * `ShapeUISchemaV2`.
 */
export const BANNER_KEY = Symbol('BannerKey');

export const CONDITIONAL_UI_SCHEMA_KEY = Symbol('ConditionalUISchemaKey');

/**
 * Allows info text for section names
 * `ShapeUISchemaV2`.
 */
export const INFO_TEXT_UI_SCHEMA_KEY = Symbol('InfoTextUISchemaKey');

/**
 * Allows collapsible sections
 * `ShapeUISchemaV2`.
 */
export const IS_COLLAPSIBLE_SCHEMA_KEY = Symbol('IsCollapsibleUISchemaKey');

/**
 * Allows expandable details for sections with header field and detail fields
 * `ShapeUISchemaV2`.
 */
export const EXPANDABLE_DETAILS_KEY = Symbol('ExpandableDetailsUISchemaKey');

/**
 * Allows defining a function to generate default field values for new shape instances.
 * This is useful when defaults depend on runtime context like configuration state or data sources.
 * `ShapeUISchemaV2`.
 */
export const FIELD_DEFAULTS_GENERATOR_KEY = Symbol('FieldDefaultsGeneratorUISchemaKey');

/**
 * Sets the number of columns to render components in. If not specified, defaults to 1 column.
 * `ShapeUISchemaV2`
 */
export const NUM_COLUMNS_KEY = Symbol('NumColumnsUISchemaKey');

export interface ConfigDataFrameInfo {
  // We don't always have access to a persisted data frame, which is why id is optional. It's needed for any
  // editor components that require using the values of the data frame.
  id?: string;
  dataFrameSpec: DataFrameSpec;
}

export type ConfigDataFramesInfos<
  TStepType extends PipelineStepType,
  TStepVersion extends number,
> = InputStepDataFramesMappedType<TStepType, TStepVersion, Array<ConfigDataFrameInfo>>;

export function getInputDataFramesFromInfos<TStepType extends PipelineStepType, TStepVersion extends number>(
  dataFramesInfos: ConfigDataFramesInfos<TStepType, TStepVersion>
): InputDataFrames<TStepType, TStepVersion> {
  return Object.fromEntries(
    Object.entries(dataFramesInfos).map(
      ([stepDataName, dataFrameInfoList]: [string, Array<ConfigDataFrameInfo>]) => [
        stepDataName,
        dataFrameInfoList.map(({dataFrameSpec}) => dataFrameSpec),
      ]
    )
    // We can't narrow the key's type in `fromEntries`.
  ) as InputDataFrames<TStepType, TStepVersion>;
}

export function getDataFrameInfosFromInputDataFrames<
  TStepType extends PipelineStepType,
  TStepVersion extends number,
>(
  dataFramesInfos: InputStepDatasRecord<TStepType, TStepVersion, PipelineStepDataKind.DATASET>
): ConfigDataFramesInfos<TStepType, TStepVersion> {
  return Object.fromEntries(
    Object.entries(dataFramesInfos).map(
      ([stepDataName, dataFrameSpecList]: [
        string,
        Array<PipelineStepRawData<PipelineStepDataKind.DATASET>>,
      ]) =>
        [stepDataName, dataFrameSpecList.map(({id, ...dataFrameSpec}) => ({id, dataFrameSpec}))] as [
          string,
          Array<ConfigDataFrameInfo>,
        ]
    )
    // We can't narrow the key's type in `fromEntries`.
  ) as ConfigDataFramesInfos<TStepType, TStepVersion>;
}

interface FieldUISchemaWithStandardComponent {
  displayName?: string;
  infoText?: string;
  fieldSettings?: StepConfigUISchema;
}

export enum StepConfigEditorOverride {
  CHECKBOX = 'Checkbox',
  ICON_SELECTOR = 'IconSelector',
  COLOR_PICKER = 'ColorPicker',
  STRING_LABEL = 'StringLabel',
  MAPPING_ARRAY = 'MappingArray',
}

export const STEP_CONFIG_EDITOR_TO_VALID_KIND: {
  [key in StepConfigEditorOverride]: Array<PipelineConfigJsonSpec['kind']>;
} = {
  [StepConfigEditorOverride.CHECKBOX]: ['boolean'],
  [StepConfigEditorOverride.ICON_SELECTOR]: ['enum'],
  [StepConfigEditorOverride.COLOR_PICKER]: ['string'],
  [StepConfigEditorOverride.STRING_LABEL]: ['string'],
  [StepConfigEditorOverride.MAPPING_ARRAY]: ['array'],
} as const;

interface StringFieldSettings {
  useTextArea?: boolean;
}

interface EnumFieldSettings {
  displayNames?: Record<string, string>;
  defaultValue?: string | number;
  renderCyclingSelector?: boolean;
  iconNames?: Record<string, BenchlingIconName>;
  svgImagePaths?: Record<string, string>;
}

interface TaggedUnionFieldSettings {
  tagKeyDisplayNames?: Record<string, string>;
  renderCyclingSelector?: boolean;
}

interface CheckboxFieldSettings {
  checkboxLabel: string;
}

/** @package */
export interface IconSelectorFieldSettings extends Pick<EnumFieldSettings, 'displayNames' | 'defaultValue'> {
  iconNames: Record<string, BenchlingIconName>;
}

interface ColumnReferenceFieldSettings {
  noColumnSelectedText?: string;
  allowSelectNull?: boolean;
  // A string with optional * wildcards to filter only columns with system names that match the selector
  systemNameWildcardSelector?: string;
  renderCyclingSelector?: boolean;
}

interface ColumnValueFieldSettings {
  noValueSelectedText?: string;
  renderCyclingSelector?: boolean;
}

interface TableReferenceFieldSettings {
  disabledColumn?: Array<string>;
  hiddenColumn?: Array<string>;
}

interface ArrayFieldSettings {
  disableAddRemove?: boolean;
}

export interface MappingArrayFieldSettings {
  /* Path to the column name that drives the mapping
   * e.g., ['chartConfig', 'parameters', 'seriesColumnName']
   */
  mappingSourcePath: ConfigShapePath;

  /* The field name in each array element that holds the mapped value
   * e.g., 'seriesValue'
   */
  mappingKey: string;

  // The display name to use for the no-series case
  noMappingDisplayName?: string;

  /* Whether to show the no-mapping editor
   * e.g., true
   */
  showNoMappingEditor?: boolean;

  /* The sentinel value to use for the no-mapping case
   * e.g., '__BENCHLING_NO_MAPPING_DEFAULT__'
   */
  noMappingSentinel?: string;
}

export interface ExpandableDetailsConfig {
  headerField: string;
  detailFields: ReadonlyArray<string>;
}

/**
 * Function that generates default field values for a shape based on runtime context.
 * Used when creating new instances of shapes (e.g., array elements) where defaults
 * depend on configuration state or external data.
 */
export type FieldDefaultsGeneratorFn = (
  config: JSONObject,
  dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>,
  index?: number
) => JSONObject;

export type FieldSettingsV2 =
  | StringFieldSettings
  | EnumFieldSettings
  | TaggedUnionFieldSettings
  | CheckboxFieldSettings
  | IconSelectorFieldSettings
  | ColumnReferenceFieldSettings
  | ColumnValueFieldSettings
  | TableReferenceFieldSettings
  | ArrayFieldSettings
  | MappingArrayFieldSettings;

export interface DisableFunctionParams {
  config: Record<string, JSONValue>;
  columns: Array<DataFrameColumnDef>;
}
export type DisableFunction = (params: DisableFunctionParams) => boolean;

/**
 * Path through configuration structure supporting both object keys (string) and array indices (number)
 * Examples:
 * - ['user', 'name'] → config.user.name
 * - ['items', 0, 'title'] → config.items[0].title
 * - ['groups', 1, 'members', 2] → config.groups[1].members[2]
 */
export type ConfigShapePath = Array<string | number>;

/** Schema of a scalar field.
 *
 * Keep in sync with `FieldUISchemaWithStandardComponentV2` (benchling/pipelines/step_type_library/manifests/types/ui_schema.yaml) on the back-end
 */
export interface FieldUISchemaWithStandardComponentV2 {
  displayName?: string;
  editorOverride?: StepConfigEditorOverride;
  infoText?: string;
  configShapePath: ConfigShapePath;
  fieldSettings?: FieldSettingsV2;
  disableFn?: DisableFunction;
  elementUISchema?: FieldUISchemaWithStandardComponentV2 | ShapeUISchemaV2;
}

export function assertTypeValidForEditorOverride(
  editorOverride: StepConfigEditorOverride,
  configShape: PipelineConfigJsonSpec
): void {
  assertAndContinue(
    STEP_CONFIG_EDITOR_TO_VALID_KIND[editorOverride].includes(configShape.kind),
    'Editor override does not match config shape kind'
  );
}

interface FieldUISchemaWithCustomComponent {
  displayName?: string;
  infoText?: string;
  disableFn?: DisableFunction;
  [CUSTOM_COMPONENT_KEY]: (props: {
    config: JSONValue;
    onChangeConfig: (newConfig: JSONValue) => void;
    configShape: PipelineConfigJsonSpec;
    disabled?: boolean;
  }) => JSX.Element;
  [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]?: (
    newConfig: JSONValue,
    oldConfig: JSONValue,
    dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>
  ) => JSONValue;
}

export interface CustomComponentEditorProps {
  config: Record<string, JSONValue>;
  onChangeConfig: (config: JSONValue, path: ConfigShapePath, newValue: JSONValue) => void;
  disabled?: boolean;
  configShape: PipelineConfigJsonSpec;
}
interface FieldUISchemaWithCustomComponentV2 {
  displayName?: string;
  infoText?: string;
  [CUSTOM_COMPONENT_KEY]: (props: CustomComponentEditorProps) => JSX.Element;
  disableFn?: DisableFunction;
}

export interface FieldUISchemaActionButton {
  /**
   * The key of the action button, it should be provided through `actions` in the `StepConfigEditorContext`.
   *
   * If the action isn't provided through the context, the button will be disabled.
   */
  key: string;
  buttonText: string;
  buttonIcon?: string;
  buttonVariant?: 'primary' | 'secondary';
  helpText?: string;
}

export interface FieldUISchemaMarkdown {
  markdown: string;
}

export interface FieldUISchemaBanner {
  text: string;
  bannerType?: InlineBannerType;
  supportingInfo?: string;
  visibilityFn?: (config: Record<string, JSONValue>) => boolean;
}

/** @package */
export interface ShapeUISchema {
  [FIELD_ORDER_UI_SCHEMA_KEY]?: ReadonlyArray<string>;
  [HIDDEN_FIELDS_KEY]?: ReadonlyArray<string>;
  [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]?: (
    newConfig: JSONValue,
    oldConfig: JSONValue,
    dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>
  ) => JSONValue;
  [HAS_BACKEND_FIELD_GENERATOR]?: boolean;
  [BANNER_KEY]?: FieldUISchemaBanner;
  [key: string]: FieldUISchemaWithStandardComponent | FieldUISchemaWithCustomComponent;
}
interface ShapeUISchemaV2SharedFields {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ReadonlyArray<string>;
  [SECTION_NAME_UI_SCHEMA_KEY]?: string;
  [SHOW_DIVIDER_LINE_KEY]?: boolean;
  [CONDITIONAL_UI_SCHEMA_KEY]?: {
    tagValue: string;
    configShapePath: ConfigShapePath;
  };
  [INFO_TEXT_UI_SCHEMA_KEY]?: string;
  [IS_COLLAPSIBLE_SCHEMA_KEY]?: boolean;
  [EXPANDABLE_DETAILS_KEY]?: ExpandableDetailsConfig;
  [NUM_COLUMNS_KEY]?: number;
  [BANNER_KEY]?: FieldUISchemaBanner;
  [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]?: (
    newValue: JSONValue,
    path: ConfigShapePath,
    oldConfig: JSONValue,
    dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>
  ) => JSONValue;
  [HAS_BACKEND_FIELD_GENERATOR]?: boolean;
  [FIELD_DEFAULTS_GENERATOR_KEY]?: FieldDefaultsGeneratorFn;
}

/** @package */
export type ShapeUISchemaV2FieldComponentValues = ShapeUISchemaV2SharedFields & {
  [key: string]:
    | FieldUISchemaWithStandardComponentV2
    | FieldUISchemaWithCustomComponentV2
    | FieldUISchemaActionButton
    | FieldUISchemaMarkdown;
};

/** @package */
export type ShapeUISchemaV2NestedShapeValues = ShapeUISchemaV2SharedFields & {
  [key: string]: ShapeUISchemaV2;
};

export type ShapeUISchemaV2 = ShapeUISchemaV2FieldComponentValues | ShapeUISchemaV2NestedShapeValues;

/** @package */
export interface EnumUISchema {
  displayNames?: Record<string | number, string>;
  infoText?: string;
  defaultValue?: string | number;
  placeholder?: string;
  renderCyclingSelector?: boolean;
  iconNames?: Record<string, BenchlingIconName>;
  svgImagePaths?: Record<string, string>;
}

/** @package */
export interface TaggedUnionUISchema {
  placeholder?: string;
  infoText?: string;
  tagKeyDisplayNames?: Record<string, string>;
  choiceSettings?: ShapeUISchema;
  renderCyclingSelector?: boolean;
  [FIELD_ORDER_UI_SCHEMA_KEY]?: ReadonlyArray<string>;
  [HIDDEN_FIELDS_KEY]?: ReadonlyArray<string>;
  [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]?: (
    newConfig: JSONValue,
    oldConfig: JSONValue,
    dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>
  ) => JSONValue;
  [HAS_BACKEND_FIELD_GENERATOR]?: boolean;
}

/** @package */
export type StepConfigUISchema = ShapeUISchema | TaggedUnionUISchema | EnumUISchema | undefined;

export type StepConfigUISchemaV2 =
  | ShapeUISchemaV2
  | FieldUISchemaWithStandardComponentV2
  | FieldUISchemaWithCustomComponentV2
  | FieldUISchemaActionButton
  | FieldUISchemaMarkdown;

export type StepConfigUISchemaWithTabsV2 = TabbedUISchema | StepConfigUISchemaV2;

export function isShapeUISchemaV2(schema: StepConfigUISchemaV2): schema is ShapeUISchemaV2 {
  return schema.hasOwnProperty(FIELD_ORDER_UI_SCHEMA_KEY);
}

export function isShapeUISchemaV2NestedShapeValues(
  schema: ShapeUISchemaV2
): schema is ShapeUISchemaV2NestedShapeValues {
  if (!schema[FIELD_ORDER_UI_SCHEMA_KEY]?.length) {
    return false;
  }
  assert(
    schema.hasOwnProperty(schema[FIELD_ORDER_UI_SCHEMA_KEY][0]),
    `Field ${schema[FIELD_ORDER_UI_SCHEMA_KEY][0]} is not defined in uiSchema`
  );
  const firstField = schema[schema[FIELD_ORDER_UI_SCHEMA_KEY][0]];
  return firstField.hasOwnProperty(FIELD_ORDER_UI_SCHEMA_KEY);
}

export function isShapeUISchemaV2FieldComponentValue(
  schema: ShapeUISchemaV2
): schema is ShapeUISchemaV2FieldComponentValues {
  if (!schema[FIELD_ORDER_UI_SCHEMA_KEY]?.length) {
    return false;
  }
  assert(
    schema.hasOwnProperty(schema[FIELD_ORDER_UI_SCHEMA_KEY][0]),
    `Field ${schema[FIELD_ORDER_UI_SCHEMA_KEY][0]} is not defined in uiSchema`
  );
  const firstField = schema[schema[FIELD_ORDER_UI_SCHEMA_KEY][0]];
  return !firstField.hasOwnProperty(FIELD_ORDER_UI_SCHEMA_KEY);
}

export function isFieldUISchemaWithStandardComponentV2(
  schema: StepConfigUISchemaV2
): schema is FieldUISchemaWithStandardComponentV2 {
  return schema != null && schema.hasOwnProperty('configShapePath');
}

export function isTabbedUISchema(schema: StepConfigUISchemaWithTabsV2): schema is TabbedUISchema {
  return schema.hasOwnProperty(TAB_ORDER_UI_SCHEMA_KEY);
}

function isFieldUISchemaWithStandardComponent(
  field: ShapeUISchema[keyof ShapeUISchema] | ShapeUISchemaV2[keyof ShapeUISchemaV2]
): field is FieldUISchemaWithStandardComponent {
  return field != null && field.hasOwnProperty('fieldSettings');
}

export function getFieldSettingsIfPresent(
  schema: ShapeUISchema | undefined,
  fieldName: string
): StepConfigUISchema | undefined {
  const field = schema?.[fieldName];
  if (isFieldUISchemaWithStandardComponent(field)) {
    return field.fieldSettings;
  }
  return undefined;
}

export function getFieldSettingsIfPresentV2(
  schema: ShapeUISchemaV2 | undefined,
  fieldName: string
): StepConfigUISchemaV2 | undefined {
  // V2 Schemas have the fieldSettings doubly nested in the field object
  // under the same fieldName
  const field = schema?.[fieldName];
  if (field != null && field.hasOwnProperty(fieldName)) {
    const nestedField = (field as ShapeUISchemaV2)[fieldName];
    if (isFieldUISchemaWithStandardComponent(nestedField)) {
      return nestedField.fieldSettings as StepConfigUISchemaV2;
    }
  }
  return undefined;
}

export function isFieldUISchemaWithCustomComponent(
  field: ShapeUISchema[keyof ShapeUISchema]
): field is FieldUISchemaWithCustomComponent {
  return field != null && field.hasOwnProperty(CUSTOM_COMPONENT_KEY);
}

export function isFieldUISchemaWithCustomComponentV2(
  field: ShapeUISchemaV2[keyof ShapeUISchemaV2]
): field is FieldUISchemaWithCustomComponentV2 {
  return field != null && field.hasOwnProperty(CUSTOM_COMPONENT_KEY);
}

export function isFieldUISchemaActionButton(
  field: ShapeUISchemaV2[keyof ShapeUISchemaV2]
): field is FieldUISchemaActionButton {
  return field != null && field.hasOwnProperty('key');
}

export function isFieldUISchemaMarkdown(
  field: ShapeUISchemaV2[keyof ShapeUISchemaV2]
): field is FieldUISchemaMarkdown {
  return field != null && field.hasOwnProperty('markdown');
}

export function isFieldUISchemaBanner(
  field: ShapeUISchemaV2[keyof ShapeUISchemaV2]
): field is FieldUISchemaBanner {
  return field != null && field.hasOwnProperty('text');
}

export function isStringFieldSettings(
  fieldSettings: FieldSettingsV2 | undefined
): fieldSettings is StringFieldSettings {
  // Note: useTextArea isn't strictly required, but because it's the only field setting at this time,
  // any instance should include it. If more field settings are added, this should be OR'd.
  return fieldSettings != null && fieldSettings.hasOwnProperty('useTextArea');
}

export function isCheckboxFieldSettings(
  fieldSettings: FieldSettingsV2 | undefined
): fieldSettings is CheckboxFieldSettings {
  return fieldSettings != null && fieldSettings.hasOwnProperty('checkboxLabel');
}

export function isIconSelectorFieldSettings(
  fieldSettings: FieldSettingsV2 | undefined
): fieldSettings is IconSelectorFieldSettings {
  return fieldSettings != null && fieldSettings.hasOwnProperty('iconNames');
}

export function isColumnReferenceFieldSettings(
  fieldSettings: FieldSettingsV2 | undefined
): fieldSettings is ColumnReferenceFieldSettings {
  return (
    fieldSettings != null &&
    (fieldSettings.hasOwnProperty('noColumnSelectedText') || fieldSettings.hasOwnProperty('allowSelectNull'))
  );
}

export function isColumnValueFieldSettings(
  fieldSettings: FieldSettingsV2 | undefined
): fieldSettings is ColumnValueFieldSettings {
  return fieldSettings != null && fieldSettings.hasOwnProperty('noValueSelectedText');
}

export function isTableReferenceFieldSettings(
  fieldSettings: FieldSettingsV2 | undefined
): fieldSettings is TableReferenceFieldSettings {
  return (
    fieldSettings != null &&
    (fieldSettings.hasOwnProperty('disabledColumn') || fieldSettings.hasOwnProperty('hiddenColumn'))
  );
}

export function isArrayFieldSettings(
  fieldSettings: FieldSettingsV2 | undefined
): fieldSettings is ArrayFieldSettings {
  return fieldSettings != null && fieldSettings.hasOwnProperty('disableAddRemove');
}

export function isMappingArrayFieldSettings(
  fieldSettings: FieldSettingsV2 | undefined
): fieldSettings is MappingArrayFieldSettings {
  return (
    fieldSettings != null &&
    fieldSettings.hasOwnProperty('mappingSourcePath') &&
    fieldSettings.hasOwnProperty('mappingKey')
  );
}

export function isDiscreteColorPalette(value: unknown): value is DiscreteColorPalette {
  return typeof value === 'string' && Object.keys(COLOR_PALETTES).includes(value);
}
